import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Icon, Swatch } from "@/components/primitives";
import type { SwatchKind } from "@/components/primitives";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { ensureConversation } from "@/hooks/useMessages";

interface ProfileHit {
  id: string;
  handle: string;
  display_name: string | null;
  avatar: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function NewMessageModal({ open, onClose }: Props) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<ProfileHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [starting, setStarting] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setHits([]);
      return;
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (debounce.current) clearTimeout(debounce.current);
    const q = query.trim().replace(/^@/, "");
    if (q.length < 2) { setHits([]); return; }
    setSearching(true);
    debounce.current = setTimeout(async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, handle, display_name, avatar")
        .or(`handle.ilike.%${q}%,display_name.ilike.%${q}%`)
        .neq("id", user?.id ?? "")
        .limit(20);
      setHits((data ?? []) as ProfileHit[]);
      setSearching(false);
    }, 250);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [query, open, user]);

  const startDM = async (other: ProfileHit) => {
    if (!user || starting) return;
    setStarting(true);
    const convoId = await ensureConversation(user.id, other.id, null);
    setStarting(false);
    if (!convoId) return;
    onClose();
    nav(`/messages/${convoId}`);
  };

  if (!open) return null;

  // Portal to body so the sheet escapes MobileFrame's local stacking
  // context (position: relative; zIndex: 5) — same pattern as the badge
  // modal and listing-status sheet.
  const sheet = (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="New message"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "hsla(0,0%,0%,0.55)",
        zIndex: 100,
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        touchAction: "none",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 430,
          background: "hsl(var(--paper))",
          borderTop: "1.5px solid hsl(var(--ink))",
          padding: "16px 16px calc(16px + env(safe-area-inset-bottom, 0px))",
          maxHeight: "85vh",
          display: "flex", flexDirection: "column",
          overscrollBehavior: "contain",
          touchAction: "pan-y",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
          >
            {Icon.chev(18, "l")}
          </button>
          <div style={{
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontWeight: 800, fontSize: 18,
            color: "hsl(var(--ink))",
          }}>
            New message
          </div>
          <div style={{ width: 30 }} />
        </div>

        {/* Search input */}
        <div style={{ marginBottom: 12 }}>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by handle or name"
            aria-label="Search collectors"
            autoCapitalize="none"
            autoCorrect="off"
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid hsl(var(--ink))",
              background: "hsl(var(--cream))",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              color: "hsl(var(--ink))",
              outline: "none",
            }}
          />
        </div>

        {/* Results */}
        <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overscrollBehavior: "contain" }}>
          {query.trim().length < 2 && (
            <div style={{
              padding: "30px 12px", textAlign: "center",
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
              color: "hsl(var(--ink-2))",
            }}>
              Type a handle or name to find a collector.
            </div>
          )}
          {query.trim().length >= 2 && searching && hits.length === 0 && (
            <div className="caps" style={{
              padding: "20px 0", textAlign: "center",
              color: "hsl(var(--ink-2))", fontSize: 10,
            }}>
              Searching…
            </div>
          )}
          {query.trim().length >= 2 && !searching && hits.length === 0 && (
            <div style={{
              padding: "30px 12px", textAlign: "center",
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13,
              color: "hsl(var(--ink-2))",
            }}>
              No collectors match "{query.trim()}".
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {hits.map((h) => (
              <button
                key={h.id}
                onClick={() => startDM(h)}
                disabled={starting}
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 10px",
                  background: "hsl(var(--cream))",
                  border: "1px solid hsl(var(--ink))",
                  cursor: starting ? "default" : "pointer",
                  textAlign: "left",
                  opacity: starting ? 0.6 : 1,
                }}
              >
                <Swatch
                  kind={(h.avatar ?? "cream") as SwatchKind}
                  style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 14,
                    color: "hsl(var(--ink))", lineHeight: 1.1,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>
                    {h.display_name || h.handle}
                  </div>
                  <div style={{
                    fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                    color: "hsl(var(--ink-2))", letterSpacing: "0.06em", marginTop: 2,
                  }}>
                    @{h.handle}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
